
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { adminAssessmentAPI } from '../services/api';
import toast from 'react-hot-toast';

const AdminAssessmentManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [skillsOverview, setSkillsOverview] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generateRuns, setGenerateRuns] = useState([]); // progress table rows for "Generate Questions"
  const [addingSkill, setAddingSkill] = useState(false);
  const [processingSkills, setProcessingSkills] = useState(new Set()); // Track skills being processed
  const generatePollersRef = useRef(new Map()); // runId -> intervalId
  const [assessmentFilters, setAssessmentFilters] = useState({
    search: '',
    difficulty: 'all',
    status: 'all',
    skill: 'all',
  });

  // Category and skills states
  const [categories, setCategories] = useState([]);
  const [skillsByCategory, setSkillsByCategory] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSkills, setLoadingSkills] = useState(false);

  // Form states
  const [generateForm, setGenerateForm] = useState({
    skill_name: '',
    difficulty_level: 'beginner',
    num_questions: 10,
    question_type: 'multiple_choice',
    duration_minutes: 30
  });


  const [skillForm, setSkillForm] = useState({
    name: '',
    description: '',
    category: '',
    difficulty_levels: ['beginner', 'intermediate', 'advanced'],
    number_of_questions: 10,
    question_selection_mode: 'random',
  });
  const [useNewSkill, setUseNewSkill] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchData();
  }, []);

  // Cleanup: stop any active pollers when component unmounts
  useEffect(() => {
    const pollers = generatePollersRef.current;
    return () => {
      pollers.forEach((intervalId) => clearInterval(intervalId));
      pollers.clear();
    };
  }, []);

  // Persist generation runs locally to avoid losing context between tab switches/page refreshes.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('adminAssessment.generateRuns.v1');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setGenerateRuns(parsed.slice(0, 10));
      }
    } catch (e) {
      console.warn('Failed to read generate runs from localStorage:', e);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem('adminAssessment.generateRuns.v1', JSON.stringify(generateRuns.slice(0, 10)));
    } catch (e) {
      // ignore quota/private-mode failures
    }
  }, [generateRuns]);

  // Update skillsByCategory for "Others" category when skillsOverview changes
  useEffect(() => {
    if (skillForm.category === 'Others') {
      const othersSkills = skillsOverview
        .filter(skill => skill.category === 'Others')
        .map(skill => ({ id: skill.skill_id, name: skill.skill_name }));
      setSkillsByCategory(othersSkills);
    }
  }, [skillsOverview, skillForm.category]);

  // Cleanup: Clear any stuck processing states when component unmounts
  useEffect(() => {
    return () => {
      // Clear all processing toasts when component unmounts
      processingSkills.forEach(skillId => {
        toast.dismiss(`processing-${skillId}`);
      });
    };
  }, [processingSkills]);


  const fetchData = async (options = {}) => {
    const { silent = false, showSpinner = true } = options;
    try {
      if (showSpinner) {
        setLoading(true);
      }

      const [skillsResponse, assessmentsResponse] = await Promise.all([
        adminAssessmentAPI.getSkillsOverview(),
        adminAssessmentAPI.getAllAssessments()
      ]);
      
      // Check if responses are successful
      if (skillsResponse.status !== 200) {
        console.error('Skills API returned non-200 status:', skillsResponse.status);
      }
      if (assessmentsResponse.status !== 200) {
        console.error('Assessments API returned non-200 status:', assessmentsResponse.status);
      }
      
      // Process skills data
      if (skillsResponse.data && skillsResponse.data.skills) {
        setSkillsOverview(skillsResponse.data.skills);
      } else {
        setSkillsOverview([]);
      }
      
      // Process assessments data
      if (assessmentsResponse.data && assessmentsResponse.data.assessments) {
        setAssessments(assessmentsResponse.data.assessments);
      } else {
        setAssessments([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (!silent) {
        toast.error(`Failed to load data: ${error.message}`);
      }
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await adminAssessmentAPI.getSkillCategories();
      
      if (response.status === 200 && response.data && response.data.categories) {
        const categoriesList = response.data.categories;
        // Check if "Others" category exists, if not add it to the list
        const hasOthers = categoriesList.some(cat => cat.name === 'Others');
        if (!hasOthers) {
          // Add "Others" category option (will be created in DB when first skill is added)
          categoriesList.push({
            id: 'others-temp',
            name: 'Others',
            description: 'Category for custom skills that don\'t fit into existing categories',
            color_code: null,
            icon_url: null
          });
        }
        
        // Sort categories: "Others" should be at the end, all others alphabetically
        const sortedCategories = categoriesList.sort((a, b) => {
          if (a.name === 'Others') return 1; // "Others" goes to end
          if (b.name === 'Others') return -1; // "Others" goes to end
          return a.name.localeCompare(b.name); // Alphabetical for others
        });
        
        setCategories(sortedCategories);
      } else {
        // If no categories exist, at least add "Others" as an option
        setCategories([{
          id: 'others-temp',
          name: 'Others',
          description: 'Category for custom skills that don\'t fit into existing categories',
          color_code: null,
          icon_url: null
        }]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(`Failed to load categories: ${error.message}`);
      // Even on error, provide "Others" as an option
      setCategories([{
        id: 'others-temp',
        name: 'Others',
        description: 'Category for custom skills that don\'t fit into existing categories',
        color_code: null,
        icon_url: null
      }]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryChange = async (categoryName) => {
    setSkillForm({...skillForm, category: categoryName, name: ''}); // Reset skill name when category changes
    setSkillsByCategory([]);
    setUseNewSkill(false);
    
    if (!categoryName) {
      return;
    }
    
    // For "Others" category, we don't need to fetch skills from API
    // We'll show skills from the overview that belong to "Others" category
    if (categoryName === 'Others') {
      setLoadingSkills(false);
      // Filter skills from overview that belong to "Others" category
      const othersSkills = skillsOverview
        .filter(skill => skill.category === 'Others')
        .map(skill => ({ id: skill.skill_id, name: skill.skill_name }));
      setSkillsByCategory(othersSkills);
      setUseNewSkill(true); // Always show input field for "Others"
      return;
    }
    
    try {
      setLoadingSkills(true);
      const response = await adminAssessmentAPI.getSkillsByCategory(categoryName);
      
      if (response.status === 200 && response.data && response.data.skills) {
        setSkillsByCategory(response.data.skills);
      } else {
        setSkillsByCategory([]);
      }
    } catch (error) {
      // If category doesn't exist (404), it's okay - just show empty list
      // The category will be created when a skill is added
      if (error.response?.status === 404) {
        console.log(`Category '${categoryName}' not found yet. It will be created when you add a skill.`);
        setSkillsByCategory([]);
      } else {
        console.error('Error fetching skills by category:', error);
        toast.error(`Failed to load skills for category: ${error.message}`);
        setSkillsByCategory([]);
      }
    } finally {
      setLoadingSkills(false);
    }
  };

  const handleGenerateQuestions = async (e) => {
    e.preventDefault();
    const selectedSkill = skillsOverview.find((s) => s.skill_name === generateForm.skill_name);
    const skillId = selectedSkill?.skill_id;
    const runId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const nowIso = new Date().toISOString();
    const newRun = {
      id: runId,
      started_at: nowIso,
      updated_at: nowIso,
      skill_id: skillId || null,
      skill_name: generateForm.skill_name,
      difficulty_level: generateForm.difficulty_level,
      num_questions: generateForm.num_questions,
      question_type: generateForm.question_type,
      duration_minutes: generateForm.duration_minutes,
      status: 'starting', // starting | requesting | processing | completed | failed
      progress_questions: null,
      progress_assessments: null,
      message: '',
      error: '',
    };

    setGenerateRuns((prev) => [newRun, ...prev].slice(0, 10));

    setGenerateRuns((prev) =>
      prev.map((r) =>
        r.id === runId ? { ...r, status: 'requesting', updated_at: new Date().toISOString() } : r
      )
    );

    const startPolling = () => {
      if (!skillId) return;
      if (generatePollersRef.current.has(runId)) return;

      const intervalId = setInterval(async () => {
        try {
          const statusResponse = await adminAssessmentAPI.getSkillStatus(skillId);
          const status = statusResponse.data?.status || statusResponse.data || {};

          setGenerateRuns((prev) =>
            prev.map((r) => {
              if (r.id !== runId) return r;
              const nextStatus = status.is_complete
                ? 'completed'
                : status.is_processing
                ? 'processing'
                : r.status;
              return {
                ...r,
                status: nextStatus,
                progress_questions:
                  typeof status.total_questions === 'number' ? status.total_questions : r.progress_questions,
                progress_assessments:
                  typeof status.total_assessments === 'number' ? status.total_assessments : r.progress_assessments,
                updated_at: new Date().toISOString(),
              };
            })
          );

          if (status.is_complete) {
            const active = generatePollersRef.current.get(runId);
            if (active) {
              clearInterval(active);
              generatePollersRef.current.delete(runId);
            }
          }
        } catch (pollError) {
          // Non-fatal: keep polling; error details shown when request fails
          console.warn('Generate Questions status poll error:', pollError);
        }
      }, 8000);

      generatePollersRef.current.set(runId, intervalId);
    };

    startPolling();

    const payload = { ...generateForm };

    void (async () => {
      try {
        const response = await adminAssessmentAPI.generateQuestions(payload);

        const successMsg =
          response?.data?.message || `Started generating questions for ${payload.skill_name}`;
        toast.success(successMsg);

        setGenerateRuns((prev) =>
          prev.map((r) =>
            r.id === runId
              ? { ...r, status: 'completed', message: successMsg, updated_at: new Date().toISOString() }
              : r
          )
        );

        await fetchData({ silent: true, showSpinner: false });

        setGenerateForm({
          skill_name: '',
          difficulty_level: 'beginner',
          num_questions: 10,
          question_type: 'multiple_choice',
          duration_minutes: 30
        });
      } catch (error) {
        console.error('Error generating questions:', error);
        const errMsg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'Failed to generate questions';
        toast.error(errMsg);

        setGenerateRuns((prev) =>
          prev.map((r) =>
            r.id === runId
              ? { ...r, status: 'failed', error: String(errMsg), updated_at: new Date().toISOString() }
              : r
          )
        );
      } finally {
        const active = generatePollersRef.current.get(runId);
        if (active) {
          clearInterval(active);
          generatePollersRef.current.delete(runId);
        }
      }
    })();
  };



  // Poll skill status to check if processing is complete
  const pollSkillStatus = async (skillId, skillName) => {
    const maxAttempts = 24; // 24 * 10 seconds = 240 seconds = 4 minutes
    let attempts = 0;
    let lastQuestionCount = 0;
    let stuckCount = 0; // Track if question count hasn't changed
    let pollInterval = null;

    const checkStatus = async () => {
      try {
        const statusResponse = await adminAssessmentAPI.getSkillStatus(skillId);
        const status = statusResponse.data?.status || statusResponse.data;
        
        console.log(`Polling status for ${skillName} (attempt ${attempts + 1}):`, {
          fullResponse: statusResponse.data,
          status: status,
          is_complete: status.is_complete,
          is_processing: status.is_processing,
          total_questions: status.total_questions,
          total_assessments: status.total_assessments
        });
        
        // Check if question count has changed
        const currentQuestionCount = status.total_questions || 0;
        if (currentQuestionCount === lastQuestionCount && status.is_processing) {
          stuckCount++;
          console.warn(`Question count hasn't changed for ${skillName} (stuck count: ${stuckCount})`);
        } else {
          stuckCount = 0; // Reset if progress is made
        }
        lastQuestionCount = currentQuestionCount;
        
        // If stuck for more than 2 checks (20 seconds with 10s interval), consider it done or error
        if (stuckCount >= 2 && currentQuestionCount > 0) {
          console.log(`Skill ${skillName} appears stuck but has ${currentQuestionCount} questions. Treating as complete.`);
          toast.dismiss(`processing-${skillId}`);
          toast.success(`✅ ${skillName}: ${currentQuestionCount} questions generated (processing appears complete).`);
          setProcessingSkills(prev => {
            const newSet = new Set(prev);
            newSet.delete(skillId);
            return newSet;
          });
          fetchData({ silent: true, showSpinner: false });
          if (pollInterval) {
            clearInterval(pollInterval);
          }
          return true;
        }
        
        // If stuck with no questions after 3 checks (30 seconds), stop and show error
        if (stuckCount >= 3 && currentQuestionCount === 0) {
          console.error(`Skill ${skillName} is stuck with no progress. Stopping polling.`);
          toast.dismiss(`processing-${skillId}`);
          toast.error(`❌ ${skillName}: Processing appears stuck. Please check the backend or try again.`);
          setProcessingSkills(prev => {
            const newSet = new Set(prev);
            newSet.delete(skillId);
            return newSet;
          });
          if (pollInterval) {
            clearInterval(pollInterval);
          }
          return true;
        }
        
        if (status.is_complete) {
          // Processing complete!
          toast.dismiss(`processing-${skillId}`);
          toast.success(`✅ ${skillName}: ${status.total_questions || 0} questions and ${status.total_assessments || 0} assessments generated!`);
          setProcessingSkills(prev => {
            const newSet = new Set(prev);
            newSet.delete(skillId);
            return newSet;
          });
          fetchData({ silent: true, showSpinner: false }); // Refresh to show new questions
          if (pollInterval) {
            clearInterval(pollInterval);
          }
          return true;
        } else if (status.is_processing) {
          // Still processing - show progress
          toast.loading(`⏳ ${skillName}: Generating questions... (${status.total_questions || 0} so far)`, {
            id: `processing-${skillId}`,
            duration: Infinity // Keep showing until explicitly dismissed
          });
        } else {
          // Not processing and not complete - might be an error or completed state
          console.warn(`Skill ${skillName} status (not processing, not complete):`, status);
          // Check if it's actually complete but status wasn't set correctly
          if (status.total_questions > 0 || status.total_assessments > 0) {
            toast.dismiss(`processing-${skillId}`);
            toast.success(`✅ ${skillName}: Processing appears complete (${status.total_questions || 0} questions, ${status.total_assessments || 0} assessments)`);
            setProcessingSkills(prev => {
              const newSet = new Set(prev);
              newSet.delete(skillId);
              return newSet;
            });
            fetchData({ silent: true, showSpinner: false });
            if (pollInterval) {
              clearInterval(pollInterval);
            }
            return true;
          } else {
            // No questions and not processing - might be an error
            console.error(`Skill ${skillName} has no questions and is not processing. Status:`, status);
            toast.dismiss(`processing-${skillId}`);
            toast.warning(`⚠️ ${skillName}: Processing stopped with no questions generated. Please check manually.`);
            setProcessingSkills(prev => {
              const newSet = new Set(prev);
              newSet.delete(skillId);
              return newSet;
            });
            if (pollInterval) {
              clearInterval(pollInterval);
            }
            return true; // Stop polling
          }
        }
        
        return false;
      } catch (error) {
        console.error('Error checking skill status:', error);
        console.error('Error response:', error.response?.data);
        // Don't increment attempts here - let the interval handle it
        // Just return false to continue polling
        return false;
      }
    };

    // Do an immediate check first
    const immediateCheck = await checkStatus();
    if (immediateCheck) {
      return; // Already complete or error
    }

    // Poll every 10 seconds (more frequent updates)
    pollInterval = setInterval(async () => {
      attempts++;
      const isComplete = await checkStatus();
      
      if (isComplete || attempts >= maxAttempts) {
        if (pollInterval) {
          clearInterval(pollInterval);
        }
        if (!isComplete && attempts >= maxAttempts) {
          toast.dismiss(`processing-${skillId}`);
          toast.error(`⏱️ ${skillName}: Still processing after ${maxAttempts * 10} seconds. The process may be stuck. Please refresh the page.`);
          setProcessingSkills(prev => {
            const newSet = new Set(prev);
            newSet.delete(skillId);
            return newSet;
          });
        }
      }
    }, 10000); // Check every 10 seconds (faster updates)
  };

  const handleToggleSkillActive = async (skillId, skillName, currentStatus) => {
    try {
      const response = await adminAssessmentAPI.toggleSkillActive(skillId);
      toast.success(response.data.message);
      
      // Refresh data to show updated status
      await fetchData({ showSpinner: false });
    } catch (error) {
      console.error('Error toggling skill status:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle skill status');
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    try {
      setAddingSkill(true);
      const response = await adminAssessmentAPI.addSkill(skillForm);
      
      // Show success message
      toast.success(response.data.message || 'Skill added successfully!');
      
      // Refresh data to show new skill in overview
      await fetchData({ showSpinner: false });
      
      // Refresh skills by category so the new skill appears in the dropdown/list
      if (skillForm.category) {
        await handleCategoryChange(skillForm.category);
      }
      
      // Reset form but keep the category
      setSkillForm({
        name: '',
        description: '',
        category: skillForm.category, // Keep the category selected
        difficulty_levels: ['beginner', 'intermediate', 'advanced'],
        number_of_questions: 10,
        question_selection_mode: skillForm.question_selection_mode,
      });
      setUseNewSkill(false);
      
      // Refresh skills by category so the new skill appears in the dropdown/list
      if (skillForm.category) {
        await handleCategoryChange(skillForm.category);
      }
      
      // Reset form but keep the category
      setSkillForm({
        name: '',
        description: '',
        category: skillForm.category, // Keep the category selected
        difficulty_levels: ['beginner', 'intermediate', 'advanced'],
        number_of_questions: 10
      });
      setUseNewSkill(false);
      
      // Start background processing for questions and assessments
      if (response.data.skill?.id) {
        const skillId = response.data.skill.id;
        const skillName = response.data.skill.name;
        
        console.log('Starting background processing for skill:', skillId);
        console.log('Selected difficulty levels:', skillForm.difficulty_levels);
        
        // Add skill to processing set
        setProcessingSkills(prev => new Set([...prev, skillId]));
        
        // Show initial processing message
        toast.loading(`⏳ ${skillName}: Generating questions and assessments...`, {
          id: `processing-${skillId}`,
          duration: Infinity // Keep showing until explicitly dismissed
        });
        
        // Call background processing endpoint
        try {
          await adminAssessmentAPI.processSkillBackground(skillId, {
            difficulty_levels: skillForm.difficulty_levels,
            number_of_questions: skillForm.number_of_questions,
            duration_minutes: skillForm.number_of_questions,
            question_selection_mode: skillForm.question_selection_mode,
          });
          
          // Start polling for status
          pollSkillStatus(skillId, skillName);
        } catch (error) {
          console.error('Error starting background processing:', error);
          toast.dismiss(`processing-${skillId}`);
          toast.error(`Failed to start processing for ${skillName}`);
          setProcessingSkills(prev => {
            const newSet = new Set(prev);
            newSet.delete(skillId);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Error adding skill:', error);
      toast.error(error.response?.data?.detail || 'Failed to add skill');
    } finally {
      setAddingSkill(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getGenerateRunStatusBadge = (run) => {
    switch (run.status) {
      case 'starting':
        return { label: 'Starting', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '⏳' };
      case 'requesting':
        return { label: 'Request sent', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '📨' };
      case 'processing':
        return { label: 'Processing', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: '⚙️' };
      case 'completed':
        return { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200', icon: '✅' };
      case 'failed':
        return { label: 'Failed', color: 'bg-red-100 text-red-800 border-red-200', icon: '❌' };
      default:
        return { label: run.status || 'Unknown', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '•' };
    }
  };

  const formatGenerateRunTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  };

  const retryGenerateRun = (run) => {
    setActiveTab('generate');
    setGenerateForm({
      skill_name: run.skill_name || '',
      difficulty_level: run.difficulty_level || 'beginner',
      num_questions: run.num_questions || 10,
      question_type: run.question_type || 'multiple_choice',
      duration_minutes: run.duration_minutes || 30,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDifficultyLevel = (difficulty) => {
    if (!difficulty) return 'Unknown';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  const getSkillStatus = (skill) => {
    if (processingSkills.has(skill.skill_id)) {
      return {
        status: 'processing',
        label: 'Processing...',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: '⏳'
      };
    } else if (skill.has_assessments) {
      return {
        status: 'ready',
        label: 'Ready',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: '✅'
      };
    } else if (skill.question_count > 0) {
      return {
        status: 'partial',
        label: 'Needs Assessment',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: '⚠️'
      };
    } else {
      return {
        status: 'empty',
        label: 'No Questions',
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: '❌'
      };
    }
  };

  const uniqueAssessmentSkills = useMemo(() => {
    const skills = new Set();
    assessments.forEach((assessment) => {
      if (assessment.skill_name) {
        skills.add(assessment.skill_name);
      }
    });
    return Array.from(skills).sort((a, b) => a.localeCompare(b));
  }, [assessments]);

  const filteredAssessments = useMemo(() => {
    const { search, difficulty, status, skill } = assessmentFilters;
    const searchTerm = search.trim().toLowerCase();

    return assessments.filter((assessment) => {
      const matchesSearch =
        !searchTerm ||
        assessment.title.toLowerCase().includes(searchTerm) ||
        (assessment.skill_name && assessment.skill_name.toLowerCase().includes(searchTerm));

      const matchesDifficulty =
        difficulty === 'all' || assessment.difficulty_level === difficulty;

      const matchesStatus =
        status === 'all' ||
        (status === 'active' ? assessment.is_active : !assessment.is_active);

      const matchesSkill =
        skill === 'all' || assessment.skill_name === skill;

      return matchesSearch && matchesDifficulty && matchesStatus && matchesSkill;
    });
  }, [assessments, assessmentFilters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (assessmentFilters.search.trim()) count += 1;
    if (assessmentFilters.difficulty !== 'all') count += 1;
    if (assessmentFilters.status !== 'all') count += 1;
    if (assessmentFilters.skill !== 'all') count += 1;
    return count;
  }, [assessmentFilters]);

  const hasActiveQuestionGeneration = useMemo(
    () => generateRuns.some((r) => ['starting', 'requesting', 'processing'].includes(r.status)),
    [generateRuns]
  );

  const handleAssessmentFilterChange = (key, value) => {
    setAssessmentFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetAssessmentFilters = () => {
    setAssessmentFilters({
      search: '',
      difficulty: 'all',
      status: 'all',
      skill: 'all',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="ml-4 text-gray-600">Loading assessment data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="ui-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Assessment Management</h2>
        </div>
        

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors border-b-2 ${
              activeTab === 'overview'
                ? 'bg-primary-50 text-primary-700 border-primary-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            Skills Overview
          </button>
          <button
            onClick={() => setActiveTab('assessments')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors border-b-2 ${
              activeTab === 'assessments'
                ? 'bg-primary-50 text-primary-700 border-primary-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            All Assessments
          </button>
          <button
            onClick={() => setActiveTab('generate')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors border-b-2 ${
              activeTab === 'generate'
                ? 'bg-primary-50 text-primary-700 border-primary-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            Generate Questions
            {hasActiveQuestionGeneration && (
              <span
                className="ml-1.5 inline-flex h-2 w-2 rounded-full bg-amber-400 animate-pulse align-middle"
                title="Generation in progress"
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('skills')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors border-b-2 ${
              activeTab === 'skills'
                ? 'bg-primary-50 text-primary-700 border-primary-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            Manage Skills
          </button>
        </div>
      </div>

      {hasActiveQuestionGeneration && activeTab !== 'generate' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          Question generation is running in the background. Open the{' '}
          <button
            type="button"
            className="font-medium text-primary-700 underline hover:text-primary-800"
            onClick={() => setActiveTab('generate')}
          >
            Generate Questions
          </button>{' '}
          tab for live progress. Other tabs stay on cached data until the refresh finishes.
        </div>
      )}

      {/* Skills Overview Tab */}
      {activeTab === 'overview' && (
        <div className="ui-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Skills Overview</h3>
            <p className="text-sm text-gray-600">All skills with questions and assessments</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skill</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assessments</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enable/Disable</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {skillsOverview.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No skills data available. Check console for errors.
                    </td>
                  </tr>
                ) : (
                  skillsOverview.map((skill) => {
                    const skillStatus = getSkillStatus(skill);
                    const isActive = skill.is_active !== undefined ? skill.is_active : true;
                    return (
                      <tr key={skill.skill_id} className={`hover:bg-gray-50 ${
                        skillStatus.status === 'processing' ? 'bg-blue-50' : !isActive ? 'bg-gray-50 opacity-75' : ''
                      }`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className={`font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                              {skill.skill_name}
                            </div>
                            {skillStatus.status === 'processing' && (
                              <div className="ml-2 animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                            )}
                            {!isActive && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
                                Disabled
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{skill.category}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            skill.question_count > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {skill.question_count} questions
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            skill.assessment_count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {skill.assessment_count} assessments
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`flex items-center px-2 py-1 text-xs font-medium rounded-full ${skillStatus.color}`}>
                            <span className="mr-1">{skillStatus.icon}</span>
                            {skillStatus.label}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleSkillActive(skill.skill_id, skill.skill_name, isActive)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              isActive ? 'bg-primary-600' : 'bg-gray-300'
                            }`}
                            aria-label={`${isActive ? 'Disable' : 'Enable'} ${skill.skill_name}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isActive ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Assessments Tab */}
      {activeTab === 'assessments' && (
        <div className="ui-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">All Assessments</h3>
              <p className="text-sm text-gray-600">
                Manage existing assessments (showing {filteredAssessments.length} of {assessments.length})
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={assessmentFilters.search}
                    onChange={(e) => handleAssessmentFilterChange('search', e.target.value)}
                    placeholder="Search by title or skill..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {assessmentFilters.search && (
                    <button
                      onClick={() => handleAssessmentFilterChange('search', '')}
                      className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Skill
                </label>
                <select
                  value={assessmentFilters.skill}
                  onChange={(e) => handleAssessmentFilterChange('skill', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All skills</option>
                  {uniqueAssessmentSkills.map((skill) => (
                    <option key={skill} value={skill}>
                      {skill}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Difficulty
                </label>
                <select
                  value={assessmentFilters.difficulty}
                  onChange={(e) => handleAssessmentFilterChange('difficulty', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Status
                </label>
                <select
                  value={assessmentFilters.status}
                  onChange={(e) => handleAssessmentFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetAssessmentFilters}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  disabled={activeFilterCount === 0}
                >
                  Clear Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skill</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Selection</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAssessments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No assessments match the selected filters. Try adjusting your criteria.
                    </td>
                  </tr>
                ) : (
                  filteredAssessments.map((assessment) => (
                    <tr key={assessment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{assessment.title}</div>
                        <p className="text-xs text-gray-500">Created {assessment.created_at ? new Date(assessment.created_at).toLocaleDateString() : 'N/A'}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{assessment.skill_name}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(assessment.difficulty_level)}`}>
                          {formatDifficultyLevel(assessment.difficulty_level)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{assessment.total_questions}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          (assessment.question_selection_mode || 'fixed') === 'random'
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-slate-100 text-slate-800'
                        }`}>
                          {(assessment.question_selection_mode || 'fixed') === 'random' ? 'Random' : 'Fixed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{assessment.duration_minutes} mins</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          assessment.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {assessment.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Generate Questions Tab */}
      {activeTab === 'generate' && (
        <div className="space-y-6">
          <div className="ui-card p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Generate Questions</h3>
              <p className="text-sm text-gray-600">
                Generate new questions for specific skills. Progress is tracked below.
              </p>
            </div>
            
            <form onSubmit={handleGenerateQuestions} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
                  <select
                    value={generateForm.skill_name}
                    onChange={(e) => setGenerateForm({...generateForm, skill_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select a skill</option>
                    {skillsOverview.map((skill) => (
                      <option key={skill.skill_id} value={skill.skill_name}>
                        {skill.skill_name} ({skill.question_count} questions)
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty Level</label>
                  <select
                    value={generateForm.difficulty_level}
                    onChange={(e) => setGenerateForm({...generateForm, difficulty_level: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Questions</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={generateForm.num_questions}
                    onChange={(e) => setGenerateForm({...generateForm, num_questions: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                  <select
                    value={generateForm.question_type}
                    onChange={(e) => setGenerateForm({...generateForm, question_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="true_false">True/False</option>
                  </select>
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Generate Questions (runs in background)
              </button>
            </form>
          </div>

          <div className="ui-card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">Generation Progress</h4>
              <p className="text-sm text-gray-600">
                Recent runs (newest first). Shows live status while generation is in progress.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skill</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Update</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {generateRuns.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No generation runs yet. Use the form above to start generating.
                      </td>
                    </tr>
                  ) : (
                    generateRuns.map((run) => {
                      const badge = getGenerateRunStatusBadge(run);
                      const isActiveRun = run.status === 'requesting' || run.status === 'processing' || run.status === 'starting';
                      return (
                        <tr key={run.id} className={isActiveRun ? 'bg-blue-50 hover:bg-blue-50' : 'hover:bg-gray-50'}>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{run.skill_name || '—'}</div>
                            <div className="text-xs text-gray-500">
                              {run.question_type === 'true_false' ? 'True/False' : 'Multiple choice'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(run.difficulty_level)}`}>
                              {formatDifficultyLevel(run.difficulty_level)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {run.num_questions || '—'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <div className="flex flex-col">
                              <span>
                                Questions: {typeof run.progress_questions === 'number' ? run.progress_questions : '—'}
                              </span>
                              <span className="text-xs text-gray-500">
                                Assessments: {typeof run.progress_assessments === 'number' ? run.progress_assessments : '—'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${badge.color}`}>
                              <span>{badge.icon}</span>
                              <span>{badge.label}</span>
                              {isActiveRun && (
                                <span className="ml-1 inline-block animate-spin rounded-full h-3 w-3 border-b border-current"></span>
                              )}
                            </div>
                            {run.error ? (
                              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                                {run.error}
                              </div>
                            ) : run.message ? (
                              <div className="mt-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md p-2">
                                {run.message}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatGenerateRunTime(run.updated_at)}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {run.status === 'failed' ? (
                              <button
                                type="button"
                                onClick={() => retryGenerateRun(run)}
                                className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 hover:bg-red-50"
                              >
                                Retry
                              </button>
                            ) : run.status === 'completed' ? (
                              <button
                                type="button"
                                onClick={() => fetchData({ showSpinner: false })}
                                className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                Refresh
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {/* Manage Skills Tab */}
      {activeTab === 'skills' && (
        <div className="ui-card p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Add New Skill</h3>
            <p className="text-sm text-gray-600">Add a new skill to the system for question generation and assessments</p>
          </div>
          
          <form onSubmit={handleAddSkill} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={skillForm.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={loadingCategories}
                >
                  <option value="">Select a category first</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {loadingCategories && (
                  <p className="mt-1 text-xs text-gray-500">Loading categories...</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skill Name *</label>
                {!skillForm.category ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500">
                    Please select a category first
                  </div>
                ) : skillForm.category === 'Others' ? (
                  <>
                    <input
                      type="text"
                      value={skillForm.name}
                      onChange={(e) => setSkillForm({...skillForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter new skill name (e.g., Custom Skill, Specialized Tool)"
                      required
                    />
                    {skillsByCategory.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">Existing skills in "Others" category:</p>
                        <div className="flex flex-wrap gap-2">
                          {skillsByCategory.map((skill) => (
                            <span
                              key={skill.id}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md border border-gray-200"
                            >
                              {skill.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="mt-2 text-xs text-blue-600">
                      💡 Add a new skill that doesn't fit into existing categories. It will be created with assessments and questions.
                    </p>
                  </>
                ) : (
                  <>
                    {loadingSkills ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                        <p className="text-sm text-gray-500">Loading skills...</p>
                      </div>
                    ) : !useNewSkill && skillsByCategory.length > 0 ? (
                      <>
                        <select
                          value={skillForm.name}
                          onChange={(e) => {
                            if (e.target.value === '__new__') {
                              setUseNewSkill(true);
                              setSkillForm({...skillForm, name: ''});
                            } else {
                              setSkillForm({...skillForm, name: e.target.value});
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        >
                          <option value="">Select a skill</option>
                          {skillsByCategory.map((skill) => (
                            <option key={skill.id} value={skill.name}>
                              {skill.name}
                            </option>
                          ))}
                          <option value="__new__">+ Add New Skill</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Select from existing skills or choose "+ Add New Skill" to create a new one
                        </p>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={skillForm.name}
                          onChange={(e) => setSkillForm({...skillForm, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Enter skill name (e.g., Python, JavaScript, React, SQL)"
                          required
                        />
                        {skillsByCategory.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setUseNewSkill(false);
                              setSkillForm({...skillForm, name: ''});
                            }}
                            className="mt-2 text-xs text-primary-600 hover:text-primary-700 underline"
                          >
                            ← Select from existing skills
                          </button>
                        )}
                        {skillsByCategory.length === 0 && (
                          <p className="mt-1 text-xs text-yellow-600">
                            No skills available in this category. Enter a new skill name to add it.
                          </p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Difficulty Levels</label>
                <div className="flex space-x-6">
                  {[
                    { value: 'beginner', label: 'Beginner', color: 'bg-green-100 text-green-800 border-green-200' },
                    { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                    { value: 'advanced', label: 'Advanced', color: 'bg-red-100 text-red-800 border-red-200' }
                  ].map((level) => (
                    <label key={level.value} className={`flex items-center px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      skillForm.difficulty_levels.includes(level.value)
                        ? `${level.color} border-current`
                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={skillForm.difficulty_levels.includes(level.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSkillForm({
                              ...skillForm,
                              difficulty_levels: [...skillForm.difficulty_levels, level.value]
                            });
                          } else {
                            setSkillForm({
                              ...skillForm,
                              difficulty_levels: skillForm.difficulty_levels.filter(d => d !== level.value)
                            });
                          }
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                      />
                      <span className="text-sm font-medium">{level.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Questions</label>
                <select
                  value={skillForm.number_of_questions}
                  onChange={(e) => setSkillForm({...skillForm, number_of_questions: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value={10}>10 questions (10 minutes)</option>
                  <option value={20}>20 questions (20 minutes)</option>
                  <option value={30}>30 questions (30 minutes)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Assessment duration: {skillForm.number_of_questions} minutes
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Assessment question selection</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="question_selection_mode"
                      value="random"
                      checked={skillForm.question_selection_mode === 'random'}
                      onChange={() => setSkillForm({ ...skillForm, question_selection_mode: 'random' })}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="font-medium text-gray-800">Random from question bank</span>
                      <span className="block text-xs text-gray-500">
                        Each attempt draws a new set from the bank when the candidate starts (needs enough questions in the bank).
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="question_selection_mode"
                      value="fixed"
                      checked={skillForm.question_selection_mode === 'fixed'}
                      onChange={() => setSkillForm({ ...skillForm, question_selection_mode: 'fixed' })}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="font-medium text-gray-800">Fixed paper</span>
                      <span className="block text-xs text-gray-500">
                        One frozen question set for the assessment; only display order changes between attempts.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={skillForm.description}
                onChange={(e) => setSkillForm({...skillForm, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="3"
                placeholder="Brief description of the skill and what it covers..."
              />
            </div>
            
            <button
              type="submit"
              disabled={addingSkill}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {addingSkill ? 'Adding Skill...' : 'Add Skill'}
            </button>
          </form>
          
          {/* Current Skills List */}
          <div className="mt-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Skills</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {skillsOverview.map((skill) => {
                const skillStatus = getSkillStatus(skill);
                return (
                  <div key={skill.skill_id} className={`border rounded-lg p-4 transition-all ${
                    skillStatus.status === 'processing' 
                      ? 'border-blue-200 bg-blue-50' 
                      : 'border-gray-200 bg-white'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{skill.skill_name}</h5>
                      <span className="text-xs text-gray-500">{skill.category}</span>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      {skill.question_count} questions • {skill.assessment_count} assessments
                    </div>
                    <div className="flex items-center justify-between">
                      <div className={`flex items-center px-3 py-1 text-xs font-medium rounded-full border ${skillStatus.color}`}>
                        <span className="mr-1">{skillStatus.icon}</span>
                        {skillStatus.label}
                      </div>
                      {skillStatus.status === 'processing' && (
                        <div className="flex items-center text-xs text-blue-600">
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600 mr-1"></div>
                          Generating...
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <span className={`text-xs font-medium ${skill.is_active !== false ? 'text-green-600' : 'text-gray-500'}`}>
                        {skill.is_active !== false ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleToggleSkillActive(skill.skill_id, skill.skill_name, skill.is_active !== false)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                          skill.is_active !== false ? 'bg-primary-600' : 'bg-gray-300'
                        }`}
                        aria-label={`${skill.is_active !== false ? 'Disable' : 'Enable'} ${skill.skill_name}`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            skill.is_active !== false ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAssessmentManagement;

